import { Music, Building2, Calendar, Megaphone, Sprout, CheckCircle, XCircle, Trash2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';
import { StatusBadge, RegistrationCard } from './AdminShared';

export const AdminRegistrationsTab = () => {
  const {
    bandRegistrations, venueRegistrations, eventSubmissions, promoterRegistrations,
    growerRegistrations, handleBandRegistrationAction, updateRegistrationStatus,
    deleteRegistration, handleGrowerApproval
  } = useAdmin();

  return (
    <div className="space-y-6">
      {/* Artist Registrations */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Music size={20} />Artist Registrations</h2>
        {bandRegistrations.filter(b => b.status === 'pending').length === 0 ? (
          <p className="text-muted-foreground">No pending artist registrations</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bandRegistrations.filter(b => b.status === 'pending').map(band => (
              <Card key={band.id} className="bg-card border-border border-secondary/30">
                <CardHeader className="pb-2 space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="text-base sm:text-lg break-words">{band.artist_name || band.band_name}</CardTitle>
                    <Badge variant="outline" className="text-xs">{band.genre}</Badge>
                  </div>
                  <CardDescription className="text-xs sm:text-sm">{band.contact_name} - {band.email}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-1 text-xs">
                    <Badge variant="outline"><MapPin size={10} className="mr-1" />{band.hometown}</Badge>
                    {band.artist_type && <Badge variant="secondary" className="text-xs">{band.artist_type}</Badge>}
                    {band.submitted_shorts?.length > 0 && <Badge variant="outline" className="text-secondary">{band.submitted_shorts.length} shorts</Badge>}
                    {band.submitted_events?.length > 0 && <Badge variant="outline" className="text-green-400">{band.submitted_events.length} events</Badge>}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-3">{band.bio}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {band.spotify_url && <span className="text-green-400">Spotify</span>}
                    {band.instagram && <span className="text-pink-400">Instagram</span>}
                    {band.youtube_channel && <span className="text-red-400">YouTube</span>}
                    {band.website && <span className="text-blue-400">Website</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">Submitted: {new Date(band.created_at).toLocaleDateString()}</p>
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handleBandRegistrationAction(band.id, 'approve')}><CheckCircle size={14} className="mr-1" /> Approve</Button>
                    <Button size="sm" variant="ghost" className="flex-1" onClick={() => handleBandRegistrationAction(band.id, 'reject')}><XCircle size={14} className="mr-1" /> Reject</Button>
                  </div>
                  <Button size="sm" variant="ghost" className="w-full text-destructive hover:text-destructive text-xs" onClick={() => handleBandRegistrationAction(band.id, 'delete')}><Trash2 size={12} className="mr-1" /> Delete</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Venue Registrations */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Building2 size={20} />Venue Registrations</h2>
        {venueRegistrations.filter(v => v.status === 'pending').length === 0 ? (
          <p className="text-muted-foreground">No pending venue registrations</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {venueRegistrations.filter(v => v.status === 'pending').map(venue => (
              <RegistrationCard key={venue.id} item={venue} type="venue"
                onApprove={(id) => updateRegistrationStatus('venue-registrations', id, 'approved')}
                onReject={(id) => updateRegistrationStatus('venue-registrations', id, 'rejected')}
                onDelete={(id) => deleteRegistration('venue-registrations', id)} />
            ))}
          </div>
        )}
      </div>

      {/* Event Submissions */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Calendar size={20} />Event Submissions</h2>
        {eventSubmissions.filter(e => e.status === 'pending').length === 0 ? (
          <p className="text-muted-foreground">No pending event submissions</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {eventSubmissions.filter(e => e.status === 'pending').map(event => (
              <RegistrationCard key={event.id} item={event} type="event"
                onApprove={(id) => updateRegistrationStatus('event-submissions', id, 'published')}
                onReject={(id) => updateRegistrationStatus('event-submissions', id, 'rejected')}
                onDelete={(id) => deleteRegistration('event-submissions', id)} />
            ))}
          </div>
        )}
      </div>

      {/* Promoter Registrations */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Megaphone size={20} />Promoter Registrations</h2>
        {promoterRegistrations.filter(p => p.status === 'pending').length === 0 ? (
          <p className="text-muted-foreground">No pending promoter registrations</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {promoterRegistrations.filter(p => p.status === 'pending').map(promoter => (
              <Card key={promoter.id} className="bg-card border-border">
                <CardHeader className="pb-2 space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="text-base sm:text-lg break-words">{promoter.company_name}</CardTitle>
                    <StatusBadge status={promoter.status} />
                  </div>
                  <CardDescription className="text-xs sm:text-sm break-all">{promoter.contact_name} - {promoter.email}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{promoter.description}</p>
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => updateRegistrationStatus('promoter-registrations', promoter.id, 'approved')}><CheckCircle size={14} className="mr-1" /> Approve</Button>
                    <Button size="sm" variant="destructive" className="flex-1" onClick={() => updateRegistrationStatus('promoter-registrations', promoter.id, 'rejected')}><XCircle size={14} className="mr-1" /> Reject</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Grower Registrations */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Sprout size={20} />Grower Registrations</h2>
        {growerRegistrations.filter(g => g.status === 'pending').length === 0 ? (
          <p className="text-muted-foreground">No pending grower registrations</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {growerRegistrations.filter(g => g.status === 'pending').map(grower => (
              <Card key={grower.id} className="bg-card border-border ring-1 ring-yellow-500/30">
                <CardHeader className="pb-2 space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="text-base sm:text-lg break-words">{grower.business_name}</CardTitle>
                    <StatusBadge status={grower.status} />
                  </div>
                  <CardDescription className="text-xs sm:text-sm break-all">{grower.owner_name} - {grower.city}, MN</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-xs capitalize">{grower.grow_type}</Badge>
                    {grower.accepts_wholesale && <Badge variant="outline" className="text-xs">Wholesale</Badge>}
                    {grower.offers_clones && <Badge variant="outline" className="text-xs">Clones</Badge>}
                    {grower.offers_seeds && <Badge variant="outline" className="text-xs">Seeds</Badge>}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{grower.description}</p>
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handleGrowerApproval(grower.id, 'approve')}><CheckCircle size={14} className="mr-1" /> Approve</Button>
                    <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleGrowerApproval(grower.id, 'reject')}><XCircle size={14} className="mr-1" /> Reject</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
